import { Injectable, OnDestroy } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { ConfigService } from '../../config';
import { injectAuthService } from '../../services/auth/provide-auth-service';
import { PermissionActions } from '../permissions/permissions.actions';
import {
  ClearUserSessionData,
  FetchUserInfo,
  InitAuth,
  SignIn,
  SignOut,
  UserSessionExpired,
} from './auth.actions';

export interface AuthStateModel {
  currentUserId: string;
  isAuthenticated: boolean;
  userSessionExpired: boolean;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    currentUserId: null,
    isAuthenticated: false,
    userSessionExpired: false,
  },
})
@Injectable()
export class AuthState implements NgxsOnInit, OnDestroy {
  /** A broadcast channel to notify other tabs about auth events.
   * Currently, it's bieng used with logout event only.
   */
  private authBroadcastChannel = new BroadcastChannel('AUTH_CHANNEL');
  private authService = injectAuthService();
  constructor(private configService: ConfigService) {}

  @Selector()
  static isAuthenticated(state: AuthStateModel) {
    return state?.isAuthenticated ?? false;
  }

  @Selector()
  static userId(state: AuthStateModel): string {
    return `${state.currentUserId}`;
  }

  @Selector()
  static userSessionExpired(state: AuthStateModel): boolean {
    return state.userSessionExpired;
  }

  ngxsOnInit({ dispatch }: StateContext<AuthStateModel>): void {
    this.authBroadcastChannel.onmessage = () => {
      dispatch(new UserSessionExpired());
    };
  }

  ngOnDestroy(): void {
    this.authBroadcastChannel.close();
  }

  @Action(InitAuth)
  initAuth({ patchState, dispatch }: StateContext<AuthStateModel>) {
    return this.authService.checkAuth().pipe(
      tap((isAuth) => {
        patchState({ isAuthenticated: isAuth });
        if (isAuth) {
          dispatch(new FetchUserInfo());
        } else {
          if (this.configService.configuration.AUTO_REDIRECT) {
            dispatch(new SignIn());
          }
        }
      }),
      catchError(() => {
        // console.error('An error occurred:', error);
        // Handle the error here. For example, you might want to dispatch an action:
        // dispatch(new AuthErrorAction(error));
        // Return an Observable that the rest of the pipe can subscribe to:
        return of(null);
      }),
    );
  }

  @Action(FetchUserInfo)
  fetchUserInfo({ patchState, dispatch }: StateContext<AuthStateModel>) {
    return this.authService.getCurrentUserId().pipe(
      take(1),
      tap((currentUserId) => patchState({ currentUserId })),
      filter((currentUserId) => !!currentUserId),
      switchMap(() =>
        // Load the user permissions:
        dispatch(new PermissionActions.FetchUserPermissions()),
      ),
    );
  }

  @Action(SignIn)
  signIn() {
    this.authService.signIn();
  }

  @Action(SignOut)
  signOut({ dispatch }: StateContext<AuthStateModel>) {
    return this.authService.signOut().pipe(
      tap(() => {
        // Clear the user session data:
        dispatch(new ClearUserSessionData());

        // Broadcast the session expired message:
        this.authBroadcastChannel.postMessage('User Session Expired!');
      }),
    );
  }

  @Action(UserSessionExpired)
  userSessionExpired({
    getState,
    patchState,
    dispatch,
  }: StateContext<AuthStateModel>) {
    // Clear the user session data:
    dispatch(new ClearUserSessionData());

    // Reset auth data in storage:
    this.authService.resetAuthData();

    // Update the `userSessionExpired` value to notify the app to reload:
    if (getState().isAuthenticated) {
      patchState({ userSessionExpired: true });
    }
  }
}
